import React from 'react';
import OfferOverlayLink from 'core/modules/OfferOverlayLink/OfferOverlayLink';
import PremierMerchantRebate from 'core/modules/PremierMerchantRebate/PremierMerchantRebate';
import ClickLink from 'core/modules/ClickLink/ClickLink';
import MerchantShopNowLinkLabel from 'core/modules/MerchantShopNowLinkLabel/MerchantShopNowLinkLabel';
import {
  addPeriodAfterOfferRolloverCopy,
} from 'core/utils/apis/responseParsers/commonParsers/addPeriodAfterOfferRolloverCopy';
import dayjs from 'core/utils/dayjs';

function InfoBarAccordionItem({
  offer, isSingle, isExpanded, handleClick, config,
}) {
  const {
    clickUrl, isDealOfTheWeek, id, merchant, assets, couponCode, disclaimer,
    isOngoing, stopDatetime,
  } = offer;

  const itemConfig = isDealOfTheWeek ? config.dealOfTheWeek : config.salesInfoBar;
  const { infoBarAccordion: infoBarAccordionConfig, shouldTrackInitialParams } = config;
  const {
    couponCodeLanguage, shopNowCTALinkLabel, dateFormat, rebateOptions, shouldShowCtaButton,
  } = infoBarAccordionConfig;

  const { dealOfTheWeekText } = config.dealOfTheWeek;

  const { api: { options: apiOptions } } = itemConfig;
  const { sectionId } = apiOptions;

  const getClassName = () => {
    const toggleStateClassName = isExpanded ? ' mn_expanded' : ' mn_collapsed';
    const dealOfTheWeekClassName = isDealOfTheWeek ? ' mn_dealOfTheWeek' : '';
    const fullWidthClassName = isSingle ? ' mn_fullWidth' : '';

    return `mn_offer${toggleStateClassName}${dealOfTheWeekClassName}${fullWidthClassName}`;
  };

  const shouldShowOfferOverlay = !!disclaimer;
  const expiredDate = dayjs.parseZone(stopDatetime).format(dateFormat);
  const rolloverCopyFormatted = addPeriodAfterOfferRolloverCopy(assets.rolloverCopy);
  const isNoElevation = !merchant.rebate.elevation;
  const ctaText =
    assets.callToActionText || <MerchantShopNowLinkLabel merchant={merchant} defaultLabel={shopNowCTALinkLabel} />;

  const renderOfferContent = () => (
    <>
      { isDealOfTheWeek && <div className="mn_dotwLabel" data-test="dotw-label">{dealOfTheWeekText}</div> }

      <div className="mn_offerWrap">
        <div className="mn_logo">
          <img src={merchant.logoUrls._120x60} alt={merchant.name} className="mn_hidden-xs mn_hidden-s mn_hidden-m" />
          <img src={merchant.logoUrls._88x31} alt={merchant.name} className="mn_hidden-l mn_hidden-xl" />
        </div>

        <div className="mn_description">
          <p className="mn_offerText">
            {rolloverCopyFormatted}
            <span className="mn_expire" data-test="merchant-offer-expire">
              {isOngoing ? <> Ongoing</> : <> Valid through {expiredDate}</>}
              <span className="mn_expireDot">.</span>
            </span>
          </p>
          <div className="mn_couponCodeAndRebate">
            { couponCode &&
              <span className="mn_couponCode mn_hidden-xs mn_hidden-s">
                {couponCodeLanguage} <span className="mn_code">{couponCode}</span>
                <span className="mn_divider" />
              </span>
            }
            <PremierMerchantRebate {...merchant.rebate} {...rebateOptions} />
          </div>
        </div>
        <div className="mn_shopNowWrap">
          <div className={`${shouldShowCtaButton ? 'mn_button mn_secondaryButton' : 'mn_shopNowCTA'}`}>
            <span>{shouldShowOfferOverlay ? 'View details' : ctaText}{' '}</span>
          </div>
        </div>
        <div className="mn_shortSummary">
          <p className="mn_summaryText">{assets.shortSummaryText}</p>
          {isNoElevation && <span className="mn_plusSign">+</span>}
          <PremierMerchantRebate {...merchant.rebate} {...rebateOptions} />
        </div>

        <div className="mn_expandIcon" />
      </div>
    </>
  );

  if (shouldShowOfferOverlay) {
    return (
      <div
        className={getClassName()}
        onClick={handleClick}
        role="button"
        tabIndex="0"
        aria-expanded={isExpanded}
        data-is-dotw={isDealOfTheWeek}
        data-content-item-id={id}
        data-merchant-name={merchant.name}
        data-test="info-bar-accordion"
      >
        <OfferOverlayLink
          offerId={id}
          dealId={id}
          sectionId={sectionId}
          merchantId={merchant.id}
          merchantName={merchant.name}
          preventOverlayShowing={!isExpanded}
          shouldTrackInitialParams={shouldTrackInitialParams}
        >
          { renderOfferContent() }
        </OfferOverlayLink>
      </div>
    );
  }

  return (
    <ClickLink
      className={getClassName()}
      onClick={handleClick}
      aria-expanded={isExpanded}
      url={clickUrl}
      title={`Opens a new window at ${merchant.name}`}
      target="_blank"
      data-is-dotw={isDealOfTheWeek}
      data-content-item-id={id}
      data-merchant-name={merchant.name}
    >
      { renderOfferContent() }
    </ClickLink>
  );
}

export default InfoBarAccordionItem;
